/**
 * Email-address input rules.
 *
 * Pure helpers, no React and no imports, so the unit tests can load this
 * without pulling in the React Native runtime.
 *
 * The field used to validate with `/^.+@.+\..+$/`, which accepts anything with
 * an at-sign and a dot. Kenneth Rudolph typed `ken.rudolph1@gmail.dom` on
 * 20 August 2026: well-formed, undeliverable, and — because the field locks
 * while "verifying" — uneditable for the twelve days that followed. A shape
 * check cannot catch that, because `.dom` is a perfectly good-looking TLD. So
 * this offers a correction instead of a verdict.
 */

/** Providers common enough that a near-miss is far likelier than a real domain. */
const DOMAIN_FIXES: Record<string, string> = {
    "gmail.co": "gmail.com",
    "gmail.con": "gmail.com",
    "gmail.dom": "gmail.com",
    "gmail.cm": "gmail.com",
    "gmail.comm": "gmail.com",
    "gmial.com": "gmail.com",
    "gmai.com": "gmail.com",
    "gmail.om": "gmail.com",
    "yahoo.con": "yahoo.com",
    "yahoo.dom": "yahoo.com",
    "yaho.com": "yahoo.com",
    "yahooo.com": "yahoo.com",
    "hotmail.con": "hotmail.com",
    "hotmial.com": "hotmail.com",
    "hotmail.dom": "hotmail.com",
    "outlook.con": "outlook.com",
    "outlok.com": "outlook.com",
    "icloud.con": "icloud.com",
    "iclod.com": "icloud.com",
    "icloud.dom": "icloud.com",
    "aol.con": "aol.com",
    "aol.dom": "aol.com"
};

/**
 * Bare TLD slips, applied to any domain. Deliberately excludes `.co`, which is
 * a real TLD that real people use, so it is only corrected above where the
 * provider makes the intent unambiguous (`gmail.co`).
 */
const TLD_FIXES: Record<string, string> = {
    con: "com",
    dom: "com",
    cmo: "com",
    comm: "com",
    ocm: "com",
    ner: "net",
    nte: "net",
    ogr: "org",
    orgg: "org"
};

/**
 * A shape check, not a deliverability check. Stricter than the old pattern in
 * the three ways that actually bite: no whitespace, exactly one at-sign, and a
 * TLD of at least two letters (so `name@gmail.` and `name@gmail` are refused).
 */
export function isPlausibleEmail(value: string): boolean {
    const email = value.trim();
    if (email.length === 0 || /\s/.test(email)) {
        return false;
    }
    const parts = email.split("@");
    if (parts.length !== 2) {
        return false;
    }
    const [local, domain] = parts;
    if (local.length === 0) {
        return false;
    }
    return /^[^.]+(\.[^.]+)*\.[A-Za-z]{2,}$/.test(domain);
}

/**
 * The address she probably meant, or null if it already looks right.
 *
 * Only ever suggests — never rewrites. A nurse whose address genuinely is
 * unusual must be able to ignore this and carry on, so the caller shows it as
 * a question ("Did you mean …?") rather than blocking the send.
 */
export function suggestEmailCorrection(value: string): string | null {
    const email = value.trim();
    const at = email.lastIndexOf("@");
    if (at <= 0 || at === email.length - 1) {
        return null;
    }
    const local = email.slice(0, at);
    const domain = email.slice(at + 1).toLowerCase();

    const whole = DOMAIN_FIXES[domain];
    if (whole) {
        return `${local}@${whole}`;
    }

    const lastDot = domain.lastIndexOf(".");
    if (lastDot > 0) {
        const tld = domain.slice(lastDot + 1);
        const fixed = TLD_FIXES[tld];
        if (fixed) {
            return `${local}@${domain.slice(0, lastDot)}.${fixed}`;
        }
    }
    return null;
}
